import type { IntlShape } from 'react-intl'

export interface FlightDisplayData {
  departureDate?: string
  arrivalDate?: string
  departureLabel?: string
  arrivalLabel?: string
  airlineLogoUrl?: string
  isReturnFlight: boolean
  isDirectFlight: boolean
}

export function deriveFlightDisplayData(flightDetails?: App.FlightOrderItemDetails): FlightDisplayData {
  const legs = flightDetails?.bookingJourney?.legs ?? []
  const allFlights = legs.flatMap(leg => leg.flights ?? [])
  const firstFlight = allFlights[0]
  const lastFlight = allFlights.at(-1)
  const outboundFlights = legs[0]?.flights ?? []
  const outboundLastFlight = outboundFlights.at(-1)

  const isReturnFlight = Boolean(
    firstFlight?.departureAirport &&
    lastFlight?.arrivalAirport &&
    firstFlight.departureAirport === lastFlight.arrivalAirport &&
    legs.length > 1,
  )

  const destinationFlight = isReturnFlight ? outboundLastFlight : lastFlight

  return {
    departureDate: firstFlight?.departingDate,
    arrivalDate: lastFlight?.arrivalDate ?? firstFlight?.departingDate,
    departureLabel: firstFlight?.departureCity ?? firstFlight?.departureAirportName ?? firstFlight?.departureAirport,
    arrivalLabel: destinationFlight?.arrivalCity ?? destinationFlight?.arrivalAirportName ?? destinationFlight?.arrivalAirport,
    airlineLogoUrl: firstFlight?.marketingAirline?.brandMarkUrl,
    isReturnFlight,
    isDirectFlight: !isReturnFlight && outboundFlights.length === 1,
  }
}

export function formatFlightFallbackTitle(intl: IntlShape): string {
  return intl.formatMessage({
    id: 'lochAQYQctLXFVrl0Mu3',
    defaultMessage: 'Flight booking',
    description: 'Fallback title for flight booking tile on chat landing page',
  })
}

export function formatFlightTypeLabel(intl: IntlShape, isReturnFlight: boolean, isDirectFlight: boolean): string {
  if (isReturnFlight) {
    return intl.formatMessage({
      id: 'fbGwD0DrcLqI6intq64g',
      defaultMessage: 'Return',
      description: 'Flight type label for flight booking tile on chat landing page',
    })
  }

  if (isDirectFlight) {
    return intl.formatMessage({
      id: '_0U8r14vFU7Ohyu9241z',
      defaultMessage: 'Direct',
      description: 'Flight type label for flight booking tile on chat landing page',
    })
  }

  return intl.formatMessage({
    id: 'F2tRxMrXzKmTc40Vgy2C',
    defaultMessage: 'One-way',
    description: 'Flight type label for one-way connecting flight booking tile on chat landing page',
  })
}

export function formatFlightRouteLabel(intl: IntlShape, departureLabel?: string, arrivalLabel?: string): string {
  if (!departureLabel || !arrivalLabel) {
    return formatFlightFallbackTitle(intl)
  }

  return intl.formatMessage(
    {
      id: 'pmeuxOyYKH5ZCNDyJkFw',
      defaultMessage: '{origin} to {destination}',
      description: 'Flight route label showing origin and destination airports in shortlisted flight card subtitle',
    },
    {
      origin: departureLabel,
      destination: arrivalLabel,
    },
  )
}
